import { useEffect, useRef, useState } from 'react';

export type Command = {
  label: string;
  run: () => void;
};

type Props = {
  open: boolean;
  commands: Command[];
  onClose: () => void;
};

const LIST_ID = 'dag-cmdk-list';
const optionId = (i: number) => `dag-cmdk-opt-${i}`;

/**
 * ⌘K / Ctrl-K command palette — jump to any page or run a quick action. Mirrors
 * the onboarding platform's palette, adapted to React Router. Controlled by the
 * navbar's ⌘K button + a global key listener; renders nothing when closed.
 */
export function CommandPalette({ open, commands, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? commands.filter((c) => c.label.toLowerCase().includes(needle))
    : commands;
  // Clamp during render so a shrinking list can never leave `active` pointing past
  // the end (which would make Enter silently no-op).
  const activeIndex = filtered.length ? Math.min(active, filtered.length - 1) : 0;

  // Open side effects: reset, focus the input, lock body scroll, and restore focus
  // to the trigger on close/unmount.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    setQuery('');
    setActive(0);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(id);
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const run = (cmd: Command | undefined) => {
    if (!cmd) return;
    cmd.run();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Tab') {
      // The input is the only focusable control — trap focus inside the dialog.
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Move relative to the clamped, *displayed* index — never the raw state,
      // which can lead the visible highlight if the list shrank.
      setActive(Math.min(activeIndex + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(filtered[activeIndex]);
    }
  };

  return (
    <div className="dag-cmdk" onClick={onClose}>
      <div
        className="dag-cmdk__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dag-cmdk__input">
          <svg className="dag-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search pages and actions…"
            aria-label="Search pages and actions"
            role="combobox"
            aria-expanded="true"
            aria-controls={LIST_ID}
            aria-activedescendant={filtered.length ? optionId(activeIndex) : undefined}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <ul className="dag-cmdk__list" id={LIST_ID} role="listbox">
          {filtered.length === 0 && <li className="dag-cmdk__empty">No matches</li>}
          {filtered.map((c, i) => (
            <li
              key={c.label}
              id={optionId(i)}
              className={`dag-cmdk__item${i === activeIndex ? ' is-active' : ''}`}
              role="option"
              aria-selected={i === activeIndex}
              onMouseEnter={() => setActive(i)}
              onClick={() => run(c)}
            >
              {c.label}
            </li>
          ))}
        </ul>
        <div className="dag-cmdk__foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
